import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Logo } from "@/components/AppLayout";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      nav("/app/automations");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Sign in" sub="Welcome back. Pick up where you left off." testid="login-page">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="text-xs tracking-widest uppercase text-zinc-500">Email</Label>
          <Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
            className="mt-2 h-11 bg-transparent border-white/10 focus:border-white/30 text-white" data-testid="login-email"/>
        </div>
        <div>
          <Label className="text-xs tracking-widest uppercase text-zinc-500">Password</Label>
          <Input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
            className="mt-2 h-11 bg-transparent border-white/10 focus:border-white/30 text-white" data-testid="login-password"/>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-11 bg-white text-black hover:bg-zinc-200 font-medium" data-testid="login-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Sign in"}
        </Button>
      </form>
      <div className="mt-6 text-sm text-zinc-500 text-center">
        New here? <Link to="/signup" className="text-white hover:underline" data-testid="login-to-signup">Create an account</Link>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ title, sub, children, testid }) {
  return (
    <div className="min-h-screen bg-black text-white grid lg:grid-cols-2" data-testid={testid}>
      <div className="hidden lg:flex relative grid-bg border-r border-white/10 p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2 w-fit text-zinc-400 hover:text-white text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="space-y-6">
          <Logo />
          <div className="font-display text-4xl tracking-tighter leading-[1.05] max-w-md">
            "I dropped my ipnova link in my Instagram bio. Bookings went up 4x in two weeks."
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/40 to-emerald-500/10 border border-emerald-500/30 grid place-items-center font-display">A</div>
            <div>
              <div className="text-sm font-medium">Anika Mehta</div>
              <div className="text-xs text-zinc-500">Owner, Bloom Salon</div>
            </div>
          </div>
        </div>
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-600">ipnova.ai — AI inquiry agents for any business</div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 text-zinc-400 mb-8 text-sm"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <div className="font-display text-3xl tracking-tighter font-medium">{title}</div>
          <div className="text-zinc-400 mt-1 mb-8 text-sm">{sub}</div>
          {children}
        </div>
      </div>
    </div>
  );
}
